import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { authClient } from "@/auth/client";
import { LoginPage } from "@/pages/LoginPage";
import { AppPage, TAB_KEYS, type TabKey } from "@/pages/AppPage";
import { AdminUsersPage } from "@/pages/AdminUsersPage";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: async () => {
    // href rather than to: `to` drops the query string, so /?data=compare landed
    // on /app with the flag gone and silently ran the default path instead.
    const search = typeof window === "undefined" ? "" : window.location.search;
    if (process.env.NODE_ENV !== "production") throw redirect({ href: `/app${search}` });
    const { data: session } = await authClient.getSession();
    throw redirect({ href: session ? `/app${search}` : "/login" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  // The active tab lives in the URL so a refresh, a bookmark and a link shared
  // with a colleague all land where the user was. An unrecognised or absent
  // value falls back rather than rendering an empty tab.
  //
  // `data` is declared here only so it survives navigation: whatever
  // validateSearch returns REPLACES the search params, so an undeclared
  // ?data=compare would be stripped the first time the tab changed.
  validateSearch: (search: Record<string, unknown>): { tab: TabKey; data?: string } => {
    const { tab, data } = search;
    return {
      tab:
        typeof tab === "string" && (TAB_KEYS as readonly string[]).includes(tab)
          ? (tab as TabKey)
          : "utilization",
      ...(typeof data === "string" ? { data } : {}),
    };
  },
  beforeLoad: async () => {
    // DEV BYPASS: skip auth in local development so Postgres is not required
    if (process.env.NODE_ENV !== "production") return;
    const { data: session } = await authClient.getSession();
    if (!session) throw redirect({ to: "/login" });
  },
  component: AppPage,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/users",
  beforeLoad: async () => {
    // DEV BYPASS: skip auth in local development so Postgres is not required
    if (process.env.NODE_ENV !== "production") return;
    const { data: session } = await authClient.getSession();
    if (!session) throw redirect({ to: "/login" });
    if (!session.user.role?.split(",").includes("admin"))
      throw redirect({ to: "/app", search: { tab: "utilization" } });
  },
  component: AdminUsersPage,
});

const routeTree = rootRoute.addChildren([indexRoute, loginRoute, appRoute, adminUsersRoute]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
